import React, { useEffect, useRef, useState } from "react";
import {
  ChevronLeft,
  Loader2,
  CheckCircle2,
  AlertTriangle,
  Bluetooth,
  BluetoothOff,
  Usb,
  RotateCw,
  ArrowRight,
  Circle,
} from "lucide-react";
import { Trans, useTranslation } from "react-i18next";
import {
  baseVersionOf,
  compareVersions,
  parseReleaseNotes,
} from "utils/firmwareRelease";
import {
  bleNamesForMac,
  isBleDfuSupported,
  isBluetoothAvailable,
  onBluetoothAvailabilityChanged,
  updateFirmwareOverBle,
  dfuLog,
} from "utils/bleDfu";
import FirmwareSource, { FirmwareNotes, formatKb } from "./FirmwareSource";

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

const btnSecondary =
  "rounded-[10px] border border-[#E5E7EB] bg-white px-4 py-2 text-sm font-medium text-[#374151] transition duration-150 hover:bg-[#F9FAFB] disabled:opacity-50";
const btnPrimary =
  "flex w-full items-center justify-center gap-2 rounded-[10px] px-4 py-3.5 text-base font-semibold text-white transition duration-150 disabled:bg-[#F3F4F6] disabled:text-[#9CA3AF]";
const primaryGradient = {
  background: "linear-gradient(135deg, #E8854A 0%, #D66B2E 100%)",
};
// The gradient is an inline style, so a disabled button drops it to go grey.
const primaryStyle = (disabled) => (disabled ? undefined : primaryGradient);

// Wireless update steps, in order. "finishing" (bootloader activating the
// image) is shown as part of "verifying".
const STAGES = [
  "enterDfu",
  "connectingApp",
  "rebooting",
  "connectingBootloader",
  "transferring",
  "verifying",
];
const BOOTLOADER_STAGES = ["connectingBootloader", "transferring"];

const NOTES_PREVIEW = 5;

/** GitHub-generated release notes as a compact list. */
export const ReleaseNotes = ({ notes, title }) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const sections = parseReleaseNotes(notes);
  const total = sections.reduce((n, s) => n + s.items.length, 0);
  if (!total) return null;
  let budget = expanded ? Infinity : NOTES_PREVIEW;
  return (
    <div>
      <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-[#9CA3AF]">
        {title}
      </p>
      <div className="space-y-3">
        {sections.map((section, i) => {
          if (budget <= 0) return null;
          const items = section.items.slice(0, budget);
          budget -= items.length;
          // GitHub's own "What's Changed" heading just repeats our title.
          const showTitle =
            section.title && !/^what.s changed$/i.test(section.title);
          return (
            <div key={i}>
              {showTitle && (
                <p className="mb-1 text-[12px] font-semibold text-[#374151]">
                  {section.title}
                </p>
              )}
              <ul className="space-y-1.5">
                {items.map((item, j) => (
                  <li
                    key={j}
                    className="flex gap-2 text-[13px] leading-snug text-[#374151]"
                  >
                    <span className="mt-[7px] h-1 w-1 flex-shrink-0 rounded-full bg-[#D1D5DB]" />
                    <span className="min-w-0">
                      {item.text}
                      {item.pr && (
                        <span className="ml-1.5 font-mono text-[11px] text-[#9CA3AF]">
                          #{item.pr}
                        </span>
                      )}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
      </div>
      {total > NOTES_PREVIEW && (
        <button
          onClick={() => setExpanded((v) => !v)}
          className="mt-2 text-[12px] font-medium text-[#E07A3A] hover:text-[#D66B2E]"
        >
          {expanded
            ? t("deviceTools.updateFirmware.showLess")
            : t("deviceTools.updateFirmware.showAll", { total })}
        </button>
      )}
    </div>
  );
};

// Operator-facing text for a failed update. `stage` is where it failed.
export const describeError = (t, error, stage, names) => {
  const k = "deviceTools.updateFirmware.errors";
  switch (error?.code) {
    case "package":
      return error.multiImage ? t(`${k}.multiImage`) : t(`${k}.package`);
    case "noMac":
      return t(`${k}.noMac`);
    case "didNotEnter":
      return t("deviceTools.updateFirmware.didNotEnter");
    case "returnedEarly":
      return t(`${k}.returnedEarly`, { version: error.version });
    case "not-found":
      return BOOTLOADER_STAGES.includes(stage)
        ? t(`${k}.bootloaderNotFound`)
        : t(`${k}.appNotFound`, { name: names?.app });
    case "ambiguous":
      return t(`${k}.ambiguous`);
    case "connect":
      return t(`${k}.connect`);
    case "buttonless":
      return t(`${k}.buttonless`);
    case "bluetooth":
      return t(`${k}.bluetooth`);
    case "disconnected":
      return t(`${k}.disconnected`);
    case "rejected":
      if ([0x08, 0x0b, 0x0c].includes(error.extended))
        return t(`${k}.badSignature`);
      if ([0x06, 0x07].includes(error.extended)) return t(`${k}.wrongTarget`);
      if (error.extended === 0x0d) return t(`${k}.tooLarge`);
      return t(`${k}.rejected`, { reason: error.message });
    default:
      return t(`${k}.failed`, { reason: error?.message || String(error) });
  }
};

/**
 * Guided firmware update. The bootloader on this hardware is BLE-only (no USB
 * stack at all — in DFU mode the device disappears from USB entirely), so the
 * transfer runs over the computer's Bluetooth (utils/bleDfu.js): flip the
 * device into DFU advertising over USB (confirmed via the state event), ask it
 * over BLE to reboot into the bootloader, stream the package, then verify the
 * version when it comes back on USB. Verification matters: downgrade
 * prevention is inert on this firmware, so a stale zip installs silently — the
 * version read-back is the only truth.
 *
 * The old nRF Connect hand-off is kept as a fallback ("manual"), with the REAL
 * bootloader advertising name — the shipped bootloader says SIT_BL_BLE, not
 * the DfuTarg the docs claim.
 */
const UpdateFirmwareFlow = ({
  devCfg,
  connected,
  commandsLocked,
  send,
  kick,
  onExit,
}) => {
  const { t } = useTranslation();
  const bleSupported = isBleDfuSupported();
  const [manual, setManual] = useState(!bleSupported);
  const [dfuPending, setDfuPending] = useState(false);
  const [dfuStuck, setDfuStuck] = useState(false);
  const [handoff, setHandoff] = useState(false); // device confirmed DFU_ADV

  // Wireless update state.
  const [pkg, setPkg] = useState(null); // { name, size, version, initPacket, firmware }
  const [pkgError, setPkgError] = useState(null);
  const [btAvailable, setBtAvailable] = useState(null);
  const [running, setRunning] = useState(false);
  const [stage, setStage] = useState(null);
  const [progress, setProgress] = useState(null); // { sent, total }
  const [failure, setFailure] = useState(null); // { error, stage }
  const [slowReturn, setSlowReturn] = useState(false);
  // When the current attempt started / the device came back updated.
  const startedAtRef = useRef(null);
  const [tookSeconds, setTookSeconds] = useState(null);
  // Bumped per attempt; a superseded attempt's late result is ignored.
  const runIdRef = useRef(0);
  // One automatic retry per update the operator starts. The bootloader
  // sometimes drops out mid-transfer and boots the old firmware (untouched —
  // the new image goes to a separate bank), and a fresh attempt from the top
  // reliably goes through.
  const autoRetriedRef = useRef(false);
  const retryTimerRef = useRef(null);
  const [autoRetrying, setAutoRetrying] = useState(false);
  useEffect(() => () => clearTimeout(retryTimerRef.current), []);

  // The version we started from — the only way to prove the update landed.
  const startFwRef = useRef(devCfg?.fw || null);
  useEffect(() => {
    if (!startFwRef.current && devCfg?.fw) startFwRef.current = devCfg.fw;
  }, [devCfg?.fw]);

  // The MAC names the device over BLE; devCfg is cleared once USB drops, so
  // keep the last one we saw.
  const macRef = useRef(devCfg?.mac || null);
  useEffect(() => {
    if (devCfg?.mac) macRef.current = devCfg.mac;
  }, [devCfg?.mac]);

  // The async update reads the live device state through this ref.
  const stateRef = useRef(devCfg?.state);
  stateRef.current = devCfg?.state;

  // Track "the device left USB" during the handoff (bootloader has no USB).
  const [wentAway, setWentAway] = useState(false);
  useEffect(() => {
    if (handoff && !connected) setWentAway(true);
  }, [handoff, connected]);

  const currentFw = devCfg?.fw || startFwRef.current;
  const cameBack = handoff && wentAway && connected && !!devCfg?.fw;
  const updated =
    cameBack && startFwRef.current && devCfg.fw !== startFwRef.current;

  useEffect(() => {
    if (!bleSupported) return undefined;
    let alive = true;
    isBluetoothAvailable().then((ok) => alive && setBtAvailable(ok));
    const unsubscribe = onBluetoothAvailabilityChanged(setBtAvailable);
    return () => {
      alive = false;
      unsubscribe();
    };
  }, [bleSupported]);

  // Once the image is in, the device should be back on USB within seconds.
  useEffect(() => {
    if (stage !== "verifying" || cameBack) return undefined;
    const timer = setTimeout(() => setSlowReturn(true), 30000);
    return () => clearTimeout(timer);
  }, [stage, cameBack]);

  const enterDfu = async () => {
    setDfuPending(true);
    setDfuStuck(false);
    await send("dfu").catch(() => {});
    await sleep(150);
    await kick?.().catch?.(() => {});
  };

  // The `dfu` command answers with a state event (IDLE → DFU_ADV). No edge
  // within 8 s means the console didn't take it.
  useEffect(() => {
    if (!dfuPending) return undefined;
    if (devCfg?.state === "DFU_ADV") {
      setDfuPending(false);
      setHandoff(true);
      return undefined;
    }
    const timer = setTimeout(() => {
      setDfuPending(false);
      setDfuStuck(true);
    }, 8000);
    return () => clearTimeout(timer);
  }, [dfuPending, devCfg?.state]);

  // Manual mode: already advertising when the flow opened (e.g. boot window)?
  useEffect(() => {
    if (manual && !handoff && devCfg?.state === "DFU_ADV") setHandoff(true);
  }, [manual, handoff, devCfg?.state]);

  const choosePackage = (next) => {
    setPkgError(null);
    setPkg(next);
  };
  const packageFailed = (message) => {
    setPkg(null);
    setPkgError(message);
  };

  // Resolves once the device reports DFU_ADV, sending `dfu` if needed.
  const waitForDfuAdvertising = async () => {
    if (stateRef.current === "DFU_ADV") return;
    await send("dfu").catch(() => {});
    await sleep(150);
    await kick?.().catch?.(() => {});
    for (let waited = 0; waited < 8000; waited += 100) {
      if (stateRef.current === "DFU_ADV") return;
      await sleep(100);
    }
    throw Object.assign(new Error("no DFU_ADV"), { code: "didNotEnter" });
  };

  // fromBootloader: a previous attempt left the device in the bootloader
  // (off USB) — reconnect to it directly instead of starting over.
  const startUpdate = async ({ fromBootloader = false, auto = false } = {}) => {
    clearTimeout(retryTimerRef.current);
    if (!auto) autoRetriedRef.current = false;
    setAutoRetrying(auto);
    const runId = ++runIdRef.current;
    let reached = fromBootloader ? "connectingBootloader" : "enterDfu";
    const onStage = (s) => {
      if (runId !== runIdRef.current) return;
      reached = s === "finishing" ? "verifying" : s;
      setStage(reached);
    };
    dfuLog(
      `start (${
        fromBootloader ? "retry from bootloader" : auto ? "auto-retry" : "full"
      }): ${pkg?.name}, device ${macRef.current}, fw ${devCfg?.fw}`
    );
    setFailure(null);
    setProgress(null);
    setSlowReturn(false);
    setRunning(true);
    setStage(reached);
    try {
      if (!bleNamesForMac(macRef.current)) {
        throw Object.assign(new Error("no MAC"), { code: "noMac" });
      }
      if (!fromBootloader) {
        startedAtRef.current = Date.now();
        startFwRef.current = devCfg?.fw || startFwRef.current;
        setWentAway(false);
        await waitForDfuAdvertising();
      }
      setHandoff(true);
      await updateFirmwareOverBle({
        mac: macRef.current,
        pkg,
        fromBootloader,
        onStage,
        onProgress: setProgress,
      });
      onStage("verifying");
    } catch (e) {
      dfuLog(`failed during ${reached}: ${e.code || e.name} - ${e.message}`);
      if (runId !== runIdRef.current) return;
      setRunning(false);
      setFailure({ error: e, stage: reached });
    }
  };

  const startUpdateRef = useRef(startUpdate);
  startUpdateRef.current = startUpdate;

  // Back on USB before the image went in: it left the bootloader (or never
  // reached it) without updating. Report that instead of a bare "Still vX".
  useEffect(() => {
    if (!running || !cameBack || stage === "verifying") return;
    dfuLog(`device came back on USB running ${devCfg.fw} during ${stage}`);
    runIdRef.current += 1;
    if (!autoRetriedRef.current) {
      // Start over from the top once the console has settled after its boot.
      autoRetriedRef.current = true;
      dfuLog("retrying the whole update automatically (once)");
      setHandoff(false);
      setWentAway(false);
      setProgress(null);
      setStage("enterDfu");
      setAutoRetrying(true);
      retryTimerRef.current = setTimeout(
        () => startUpdateRef.current({ auto: true }),
        2500
      );
      return;
    }
    setRunning(false);
    setFailure({
      error: { code: "returnedEarly", version: devCfg.fw },
      stage,
    });
  }, [running, cameBack, stage, devCfg?.fw]);

  useEffect(() => {
    if (updated && startedAtRef.current && tookSeconds == null) {
      setTookSeconds(Math.round((Date.now() - startedAtRef.current) / 1000));
    }
  }, [updated, tookSeconds]);

  const resetToArm = () => {
    runIdRef.current += 1;
    clearTimeout(retryTimerRef.current);
    setAutoRetrying(false);
    setRunning(false);
    setStage(null);
    setProgress(null);
    setFailure(null);
    setHandoff(false);
    setWentAway(false);
    setDfuStuck(false);
  };

  // Compare the internal versions: "v1.2.0 (v0.6.41)", "v0.6.41-dev+abc"
  // and "v0.6.41" are all the same code.
  const versionOrder = compareVersions(
    pkg?.release?.baseVersion || baseVersionOf(pkg?.version),
    baseVersionOf(currentFw)
  );
  const pkgVersionNote =
    versionOrder === 0
      ? t("deviceTools.updateFirmware.sameAsDevice")
      : versionOrder === -1
      ? t("deviceTools.updateFirmware.olderThanDevice", {
          version: currentFw,
        })
      : null;

  const release = pkg?.release;

  const transferBusy = running && stage !== "verifying";
  const startDisabled =
    !pkg || !connected || commandsLocked || btAvailable === false;

  return (
    <div className="mx-auto flex h-full max-w-2xl flex-col">
      {/* Header */}
      <div className="mb-5 flex items-center gap-3">
        <button
          onClick={onExit}
          disabled={transferBusy}
          className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg border border-[#E5E7EB] bg-white text-[#6B7280] transition duration-150 hover:bg-[#F9FAFB] hover:text-[#374151] disabled:opacity-40"
          title={t("deviceTools.shared.backToDevice")}
          aria-label={t("deviceTools.shared.backToDevice")}
        >
          <ChevronLeft size={18} />
        </button>
        <div className="min-w-0 flex-1">
          <h2 className="text-xl font-semibold text-[#111827]">
            {t("deviceTools.updateFirmware.title")}
          </h2>
          {!currentFw && (
            <p className="truncate text-[13px] text-[#6B7280]">
              {t("deviceTools.updateFirmware.readingDevice")}
            </p>
          )}
        </div>
      </div>

      <div className="flex min-h-0 flex-1 flex-col gap-3 [justify-content:safe_center]">
        {/* ── Result: device came back ── */}
        {cameBack && !failure && updated ? (
          <div className="overflow-hidden rounded-xl border border-[#D1E7DD] bg-white">
            <div className="flex items-center gap-3 bg-[#F0FDF4] px-5 py-4">
              <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-white">
                <CheckCircle2 size={22} className="text-[#16A34A]" />
              </span>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-[#14532D]">
                  {t("deviceTools.updateFirmware.updatedTitle")}
                </p>
                <p className="mt-0.5 flex flex-wrap items-center gap-1.5 text-[12px]">
                  <span className="font-mono text-[#6B7280]">
                    {startFwRef.current}
                  </span>
                  <ArrowRight size={12} className="text-[#9CA3AF]" />
                  <span className="font-mono font-semibold text-[#14532D]">
                    {devCfg.fw}
                  </span>
                  {tookSeconds != null && (
                    <span className="text-[#6B7280]">
                      ·{" "}
                      {t("deviceTools.updateFirmware.took", {
                        seconds: tookSeconds,
                      })}
                    </span>
                  )}
                </p>
              </div>
            </div>
            {release?.notes && (
              <FirmwareNotes
                notes={release.notes}
                className="border-t border-[#F3F4F6] px-5 py-4"
              />
            )}
            <div className="flex justify-end border-t border-[#F3F4F6] px-5 py-3">
              <button
                onClick={onExit}
                className="rounded-[10px] px-5 py-2 text-sm font-semibold text-white transition duration-150"
                style={primaryGradient}
              >
                {t("common.actions.done")}
              </button>
            </div>
          </div>
        ) : cameBack && !failure ? (
          <div className="rounded-xl border border-[#FDE68A] bg-[#FFFBEB] p-5 text-center">
            <AlertTriangle size={26} className="mx-auto text-[#B45309]" />
            <p className="mt-2 text-sm font-semibold text-[#92400E]">
              {t("deviceTools.updateFirmware.stillVersion", {
                version: devCfg.fw,
              })}
            </p>
            <p className="mx-auto mt-1 max-w-sm text-[13px] text-[#B45309]">
              {t("deviceTools.updateFirmware.sameVersionHint")}
            </p>
            <div className="mt-4 flex items-center justify-center gap-2">
              <button
                onClick={resetToArm}
                className="flex items-center gap-1.5 rounded-[10px] border border-[#E5E7EB] bg-white px-4 py-2 text-sm font-semibold text-[#374151] transition duration-150 hover:bg-[#F9FAFB]"
              >
                <RotateCw size={14} />
                {t("common.actions.tryAgain")}
              </button>
              <button onClick={onExit} className={btnSecondary}>
                {t("common.actions.done")}
              </button>
            </div>
          </div>
        ) : failure ? (
          /* ── Wireless update failed ── */
          <div className="rounded-xl border border-[#FECACA] bg-[#FEF2F2] p-5">
            <div className="flex items-start gap-2.5">
              <AlertTriangle
                size={18}
                className="mt-0.5 flex-shrink-0 text-[#DC2626]"
              />
              <div className="min-w-0">
                <p className="text-sm font-semibold text-[#991B1B]">
                  {t("deviceTools.updateFirmware.failedTitle")}
                </p>
                <p className="mt-1 text-[13px] leading-relaxed text-[#B91C1C]">
                  {describeError(
                    t,
                    failure.error,
                    failure.stage,
                    bleNamesForMac(macRef.current)
                  )}
                </p>
              </div>
            </div>
            <div className="mt-4 flex flex-wrap items-center gap-2">
              {/* Off USB after the reboot step = waiting in the bootloader. */}
              {!connected &&
              (BOOTLOADER_STAGES.includes(failure.stage) ||
                failure.stage === "rebooting") ? (
                <button
                  onClick={() => startUpdate({ fromBootloader: true })}
                  disabled={!pkg}
                  className="flex items-center gap-1.5 rounded-[10px] px-4 py-2 text-sm font-semibold text-white transition duration-150 disabled:opacity-50"
                  style={primaryGradient}
                >
                  <RotateCw size={14} />
                  {t("deviceTools.updateFirmware.retryTransfer")}
                </button>
              ) : (
                <button
                  onClick={() => startUpdate()}
                  disabled={!pkg || !connected || commandsLocked}
                  className="flex items-center gap-1.5 rounded-[10px] px-4 py-2 text-sm font-semibold text-white transition duration-150 disabled:opacity-50"
                  style={primaryGradient}
                >
                  <RotateCw size={14} />
                  {t("common.actions.tryAgain")}
                </button>
              )}
              <button onClick={resetToArm} className={btnSecondary}>
                {t("deviceTools.updateFirmware.changePackage")}
              </button>
              <button
                onClick={() => {
                  resetToArm();
                  setManual(true);
                }}
                className="ml-auto text-[12px] font-medium text-[#6B7280] underline-offset-2 hover:text-[#374151] hover:underline"
              >
                {t("deviceTools.updateFirmware.useNrfConnect")}
              </button>
            </div>
          </div>
        ) : running ? (
          /* ── Wireless update in progress ── */
          <div className="rounded-xl border border-[#F3F4F6] bg-white p-5">
            <div className="mb-4 flex items-center gap-2.5">
              <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-[#EFF6FF]">
                <Bluetooth size={17} className="text-[#2563EB]" />
              </span>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-[#111827]">
                  {t("deviceTools.updateFirmware.updatingTitle")}
                </p>
                <p className="truncate text-[12px] text-[#6B7280]">
                  {pkg?.name}
                </p>
              </div>
            </div>
            <ol className="space-y-2.5">
              {STAGES.map((s, i) => {
                const current = STAGES.indexOf(stage);
                const done = i < current;
                const active = i === current;
                return (
                  <li key={s} className="flex items-start gap-2.5">
                    <span className="mt-0.5 flex-shrink-0">
                      {done ? (
                        <CheckCircle2 size={16} className="text-[#16A34A]" />
                      ) : active ? (
                        <Loader2
                          size={16}
                          className="animate-spin text-[#E07A3A]"
                        />
                      ) : (
                        <Circle size={16} className="text-[#D1D5DB]" />
                      )}
                    </span>
                    <div className="min-w-0 flex-1">
                      <p
                        className={`text-[13px] ${
                          active
                            ? "font-semibold text-[#111827]"
                            : done
                            ? "text-[#374151]"
                            : "text-[#9CA3AF]"
                        }`}
                      >
                        {t(`deviceTools.updateFirmware.stages.${s}`)}
                      </p>
                      {active && s === "transferring" && progress && (
                        <div className="mt-1.5">
                          <div className="h-1.5 overflow-hidden rounded-full bg-[#F3F4F6]">
                            <div
                              className="h-full rounded-full bg-[#E07A3A] transition-all duration-150"
                              style={{
                                width: `${
                                  (progress.sent / progress.total) * 100
                                }%`,
                              }}
                            />
                          </div>
                          <p className="mt-1 text-[11px] text-[#9CA3AF]">
                            {t("deviceTools.updateFirmware.progress", {
                              percent: Math.floor(
                                (progress.sent / progress.total) * 100
                              ),
                              sent: formatKb(progress.sent),
                              total: formatKb(progress.total),
                            })}
                          </p>
                        </div>
                      )}
                      {active && s === "verifying" && (
                        <p className="mt-0.5 text-[11px] text-[#9CA3AF]">
                          {slowReturn
                            ? t("deviceTools.updateFirmware.slowReturn")
                            : t("deviceTools.updateFirmware.waitingReturn")}
                        </p>
                      )}
                    </div>
                  </li>
                );
              })}
            </ol>
            {autoRetrying && (
              <p className="mt-4 rounded-lg bg-[#FEF3E7] px-3 py-2.5 text-[12px] text-[#B45309]">
                {t("deviceTools.updateFirmware.autoRetrying")}
              </p>
            )}
            {transferBusy && (
              <p className="mt-4 rounded-lg bg-[#F5F4F1] px-3 py-2.5 text-[12px] text-[#6B7280]">
                {t("deviceTools.updateFirmware.keepClose")}
              </p>
            )}
          </div>
        ) : manual && handoff ? (
          /* ── Handoff: the BLE transfer happens in nRF Connect ── */
          <div className="rounded-xl border border-[#F3F4F6] bg-white p-5">
            <div className="mb-3 flex items-center gap-2.5">
              <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-[#EFF6FF]">
                <Bluetooth size={17} className="text-[#2563EB]" />
              </span>
              <div>
                <p className="text-sm font-semibold text-[#111827]">
                  {t("deviceTools.updateFirmware.inUpdateMode")}
                </p>
                <p className="text-[12px] text-[#6B7280]">
                  {t("deviceTools.updateFirmware.bluetoothOnly")}
                </p>
              </div>
            </div>
            <ol className="space-y-2 text-[13px] text-[#374151]">
              <li className="flex gap-2">
                <span className="font-semibold text-[#9CA3AF]">1.</span>
                <span>
                  <Trans
                    i18nKey="deviceTools.updateFirmware.step1"
                    components={{
                      mono: <span className="font-mono text-[12px]" />,
                    }}
                  />
                </span>
              </li>
              <li className="flex gap-2">
                <span className="font-semibold text-[#9CA3AF]">2.</span>
                <span>
                  <Trans
                    i18nKey="deviceTools.updateFirmware.step2"
                    components={{
                      bold: <span className="font-medium" />,
                      code: (
                        <span className="rounded bg-[#F5F4F1] px-1 font-mono text-[12px]" />
                      ),
                    }}
                  />
                </span>
              </li>
              <li className="flex gap-2">
                <span className="font-semibold text-[#9CA3AF]">3.</span>
                <span>
                  <Trans
                    i18nKey="deviceTools.updateFirmware.step3"
                    components={{ bold: <span className="font-medium" /> }}
                  />
                </span>
              </li>
            </ol>

            <div
              className={`mt-4 flex items-center gap-2 rounded-lg px-3 py-2.5 text-[12px] font-medium ${
                connected
                  ? "bg-[#F5F4F1] text-[#6B7280]"
                  : "bg-[#EFF6FF] text-[#1D4ED8]"
              }`}
            >
              {connected ? (
                <>
                  <Usb size={14} />
                  {t("deviceTools.updateFirmware.waitingUsbDrop")}
                </>
              ) : (
                <>
                  <Loader2 size={14} className="animate-spin" />
                  {t("deviceTools.updateFirmware.leftUsb")}
                </>
              )}
            </div>
            <p className="mt-2 text-[11px] text-[#9CA3AF]">
              {t("deviceTools.updateFirmware.timeoutHint")}
            </p>
          </div>
        ) : manual ? (
          /* ── Arm (nRF Connect) ── */
          <div className="rounded-xl border border-[#F3F4F6] bg-white p-6">
            <p className="mb-5 text-[14px] leading-relaxed text-[#374151]">
              <Trans
                i18nKey="deviceTools.updateFirmware.intro"
                components={{
                  mono: <span className="font-mono text-[12px]" />,
                  bold: <span className="font-medium text-[#374151]" />,
                }}
              />
            </p>

            {dfuStuck && (
              <p className="mb-4 text-[13px] text-[#DC2626]">
                {t("deviceTools.updateFirmware.didNotEnter")}
              </p>
            )}

            <button
              onClick={enterDfu}
              disabled={!connected || commandsLocked || dfuPending}
              className={btnPrimary}
              style={primaryStyle(!connected || commandsLocked || dfuPending)}
            >
              {dfuPending ? (
                <>
                  <Loader2 size={15} className="animate-spin" />
                  {t("deviceTools.updateFirmware.entering")}
                </>
              ) : (
                <>
                  <Bluetooth size={15} />
                  {t("deviceTools.updateFirmware.enter")}
                </>
              )}
            </button>
            {bleSupported && (
              <button
                onClick={() => setManual(false)}
                className="mt-4 w-full text-center text-[13px] font-medium text-[#6B7280] underline-offset-2 hover:text-[#374151] hover:underline"
              >
                {t("deviceTools.updateFirmware.useWireless")}
              </button>
            )}
          </div>
        ) : (
          /* ── Arm (wireless) ── */
          <div className="rounded-xl border border-[#F3F4F6] bg-white p-6">
            <FirmwareSource
              pkg={pkg}
              onPackage={choosePackage}
              onError={packageFailed}
              currentVersion={currentFw}
            />

            {pkgError && (
              <p className="mt-4 text-[13px] text-[#DC2626]">{pkgError}</p>
            )}
            {pkgVersionNote && (
              <p className="mt-4 flex items-start gap-2 text-[13px] text-[#B45309]">
                <AlertTriangle size={14} className="mt-0.5 flex-shrink-0" />
                {pkgVersionNote}
              </p>
            )}
            {btAvailable === false && (
              <div className="mt-4 flex items-start gap-2 rounded-lg bg-[#FEF3E7] px-3 py-2.5 text-[13px] text-[#9A3412]">
                <BluetoothOff size={15} className="mt-0.5 flex-shrink-0" />
                <span>{t("deviceTools.updateFirmware.bluetoothOff")}</span>
              </div>
            )}

            <button
              onClick={() => startUpdate()}
              disabled={startDisabled}
              className={`${btnPrimary} mt-6`}
              style={primaryStyle(startDisabled)}
            >
              <Bluetooth size={16} />
              {pkg?.label
                ? t("deviceTools.updateFirmware.startTo", {
                    version: pkg.label,
                  })
                : t("deviceTools.updateFirmware.start")}
            </button>
            <button
              onClick={() => setManual(true)}
              className="mt-4 w-full text-center text-[13px] font-medium text-[#9CA3AF] underline-offset-2 transition duration-150 hover:text-[#374151] hover:underline"
            >
              {t("deviceTools.updateFirmware.useNrfConnect")}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default UpdateFirmwareFlow;
